import React, { useState, useEffect, useRef, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { 
  Plus, Download, Eye, Trash2, Calendar, CheckCircle, Circle,
  Upload, Loader2, ChevronDown, ChevronUp, File, Paperclip,
  Pencil, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { adjuntosAPI, tareasAPI } from '../api';
import { toast } from '../hooks/use-toast';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const prioridadColors = {
  alta: 'bg-red-100 text-red-800',
  media: 'bg-yellow-100 text-yellow-800',
  baja: 'bg-green-100 text-green-800'
};

// Componente para adjuntos de tarea
const TareaAdjuntos = ({ tareaId, adjuntos, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo_adjunto', 'documento');
      try {
        await adjuntosAPI.uploadTarea(tareaId, formData);
        successCount++;
      } catch (error) {
        toast({ title: "Error", description: `No se pudo subir ${file.name}`, variant: "destructive" });
      }
    }
    
    if (successCount > 0) {
      toast({ title: `${successCount} archivo(s) subido(s)` });
      onUpdate();
    }
    setUploading(false);
  };

  const handleDownload = async (adjunto) => {
    try {
      const response = await adjuntosAPI.download(adjunto.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', adjunto.nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo descargar", variant: "destructive" });
    }
  };

  const handleDelete = async (adjuntoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await adjuntosAPI.delete(adjuntoId);
      toast({ title: "Archivo eliminado" });
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  return (
    <div className="mt-3 pt-3 border-t">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-600 flex items-center gap-1">
          <Paperclip className="w-4 h-4" /> 
          {adjuntos?.length > 0 ? `Adjuntos (${adjuntos.length})` : 'Sin adjuntos'}
        </span>
        <input type="file" ref={fileInputRef} className="hidden" multiple onChange={(e) => handleUpload(Array.from(e.target.files || []))} />
        <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          Adjuntar
        </Button>
      </div>
      {adjuntos?.length > 0 && (
        <div className="space-y-1">
          {adjuntos.map((adj) => (
            <div key={adj.id} className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-sm">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <File className="w-4 h-4 text-gray-400" />
                <button onClick={() => window.open(`/preview/${adj.id}`, '_blank')} className="text-blue-600 hover:underline truncate">{adj.nombre}</button>
                <span className="text-gray-400 text-xs">({formatFileSize(adj.tamano)})</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open(`/preview/${adj.id}`, '_blank')}><Eye className="w-4 h-4 text-green-600" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownload(adj)}><Download className="w-4 h-4 text-blue-600" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(adj.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TareasSection = ({ expedienteId, tareas: initialTareas, onRefresh }) => {
  const [tareas, setTareas] = useState(initialTareas || []);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');
  const [adjuntosTemp, setAdjuntosTemp] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tareasConAdjuntos, setTareasConAdjuntos] = useState({});
  const [expandedTareas, setExpandedTareas] = useState(new Set());
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTareas();
  }, [expedienteId]);

  const loadTareas = async () => {
    try {
      const response = await tareasAPI.getByExpediente(expedienteId);
      setTareas(response.data);
      loadAdjuntosTareas(response.data);
    } catch (error) {
      console.error('Error cargando tareas:', error);
    }
  };

  const loadAdjuntosTareas = useCallback(async (tareasData) => {
    const adjuntosMap = {};
    for (const t of (tareasData || tareas)) {
      try {
        const response = await adjuntosAPI.getByTarea(t.id);
        adjuntosMap[t.id] = response.data;
      } catch (error) {
        adjuntosMap[t.id] = [];
      }
    }
    setTareasConAdjuntos(adjuntosMap);
  }, [tareas]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData(e.target);
    
    try {
      const response = await tareasAPI.create(expedienteId, {
        titulo: formData.get('titulo'),
        descripcion: descripcion,
        fecha_limite: formData.get('fecha_limite') || null,
        prioridad: formData.get('prioridad') || 'media'
      });
      
      if (adjuntosTemp.length > 0 && response.data?.id) {
        for (const file of adjuntosTemp) {
          const uploadData = new FormData();
          uploadData.append('file', file);
          uploadData.append('tipo_adjunto', 'documento');
          await adjuntosAPI.uploadTarea(response.data.id, uploadData);
        }
      }
      
      toast({ title: "Tarea creada" });
      setIsDialogOpen(false);
      setDescripcion('');
      setAdjuntosTemp([]);
      loadTareas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear la tarea", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (tarea, e) => {
    e.stopPropagation();
    setEditingTarea(tarea);
    setEditDescripcion(tarea.descripcion || '');
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData(e.target);
    
    try {
      await tareasAPI.update(editingTarea.id, {
        titulo: formData.get('titulo'),
        descripcion: editDescripcion,
        fecha_limite: formData.get('fecha_limite') || null,
        prioridad: formData.get('prioridad')
      });
      toast({ title: "Tarea actualizada" });
      setIsEditDialogOpen(false);
      setEditingTarea(null);
      loadTareas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo actualizar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async (tarea, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar "${tarea.titulo}"?`)) return;
    try {
      await tareasAPI.delete(tarea.id);
      toast({ title: "Tarea eliminada" });
      loadTareas();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  const handleToggleComplete = async (tarea, e) => {
    e.stopPropagation();
    try {
      await tareasAPI.update(tarea.id, { completada: !tarea.completada });
      loadTareas();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const toggleTarea = (id) => {
    setExpandedTareas(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800"><Plus className="w-4 h-4 mr-2" />NUEVA TAREA</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nueva tarea</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div><Label>Título *</Label><Input name="titulo" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Fecha límite</Label><Input name="fecha_limite" type="date" /></div>
                  <div>
                    <Label>Prioridad</Label>
                    <Select name="prioridad" defaultValue="media">
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="baja">Baja</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div data-color-mode="light">
                  <Label>Descripción</Label>
                  <MDEditor value={descripcion} onChange={setDescripcion} preview="edit" height={150} />
                </div>
                <div>
                  <Label className="flex items-center gap-2"><Paperclip className="w-4 h-4" /> Archivos adjuntos</Label>
                  <input type="file" ref={fileInputRef} className="hidden" multiple onChange={(e) => setAdjuntosTemp(prev => [...prev, ...Array.from(e.target.files || [])])} />
                  <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Haz clic para seleccionar archivos</p>
                  </div>
                  {adjuntosTemp.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {adjuntosTemp.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1 px-2 bg-gray-100 rounded text-sm">
                          <span className="truncate">{file.name}</span>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setAdjuntosTemp(prev => prev.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); setDescripcion(''); setAdjuntosTemp([]); }}>Cancelar</Button>
                <Button type="submit" className="bg-blue-600" disabled={saving}>{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Editar tarea</DialogTitle></DialogHeader>
            {editingTarea && (
              <form onSubmit={handleEditSubmit}>
                <div className="space-y-4">
                  <div><Label>Título *</Label><Input name="titulo" defaultValue={editingTarea.titulo} required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Fecha límite</Label><Input name="fecha_limite" type="date" defaultValue={editingTarea.fecha_limite?.split('T')[0] || ''} /></div>
                    <div>
                      <Label>Prioridad</Label>
                      <Select name="prioridad" defaultValue={editingTarea.prioridad || 'media'}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alta">Alta</SelectItem>
                          <SelectItem value="media">Media</SelectItem>
                          <SelectItem value="baja">Baja</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div data-color-mode="light">
                    <Label>Descripción</Label>
                    <MDEditor value={editDescripcion} onChange={setEditDescripcion} preview="edit" height={150} />
                  </div>
                </div>
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-blue-600" disabled={saving}>{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar cambios</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {tareas.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No hay tareas</p>
        ) : (
          tareas.map((tarea) => {
            const isExpanded = expandedTareas.has(tarea.id);
            const adjuntos = tareasConAdjuntos[tarea.id] || [];
            
            return (
              <div key={tarea.id} className={`border rounded-lg overflow-hidden ${tarea.completada ? 'opacity-60' : ''}`}>
                <div className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => toggleTarea(tarea.id)}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <button onClick={(e) => handleToggleComplete(tarea, e)} className="mt-1">
                        {tarea.completada ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 text-gray-400" />}
                      </button>
                      <div className="flex-1">
                        <h4 className={`font-medium ${tarea.completada ? 'line-through text-gray-500' : 'text-gray-900'} flex items-center gap-2`}>
                          {tarea.titulo}
                          <Badge className={prioridadColors[tarea.prioridad]}>{tarea.prioridad}</Badge>
                          {adjuntos.length > 0 && <Badge variant="secondary"><Paperclip className="w-3 h-3 mr-1" />{adjuntos.length}</Badge>}
                        </h4>
                        {tarea.fecha_limite && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {new Date(tarea.fecha_limite).toLocaleDateString('es-ES')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => handleEditClick(tarea, e)}><Pencil className="w-4 h-4 text-blue-600" /></Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => handleDeleteClick(tarea, e)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</Button>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    {tarea.descripcion && (
                      <div className="mt-3" data-color-mode="light">
                        <p className="text-sm font-medium text-gray-700 mb-2">Descripción:</p>
                        <div className="bg-white p-3 rounded border prose prose-sm max-w-none">
                          <MDEditor.Markdown source={tarea.descripcion} />
                        </div>
                      </div>
                    )}
                    <TareaAdjuntos tareaId={tarea.id} adjuntos={adjuntos} onUpdate={() => loadAdjuntosTareas(tareas)} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TareasSection;
